import {$isMentionNode} from 'components/DSYS/editor/nodes/MentionNode';
import {$getRoot, $isElementNode, type LexicalNode} from 'lexical';
import {useMemo, useRef} from 'react';

/** A person picked from a chat composer's @mention typeahead. */
export type ChatMention = {
  /** Employee ID (`Employee.user_id`). */
  employee_id: string;
  name: string;
  ldap: string;
};

const nodeText = (node: LexicalNode): string => {
  // A MentionNode's own text is name + LDAP; the question should read "@Name".
  if ($isMentionNode(node)) {
    return `@${node.getTrigger()}`;
  }
  if (!$isElementNode(node)) {
    return node.getTextContent();
  }
  // Same block joins as Lexical's ElementNode.getTextContent.
  const children = node.getChildren();
  return children
    .map(
      (child, i) =>
        nodeText(child) +
        ($isElementNode(child) && !child.isInline() && i < children.length - 1 ? '\n\n' : '')
    )
    .join('');
};

/** Composer text with each mention written as `@Name`. Call inside a Lexical read. */
export const $getChatComposerText = (): string => nodeText($getRoot());

/** People mentioned in the editor, in document order. Call inside a Lexical read. */
export const $getChatMentions = (): ChatMention[] => {
  const mentions: ChatMention[] = [];
  const visit = (node: LexicalNode) => {
    if ($isMentionNode(node)) {
      const {id, mentionType, trigger, value} = node.exportJSON();
      if (mentionType === 'employee' && !mentions.some((m) => m.employee_id === id)) {
        mentions.push({employee_id: id, name: trigger, ldap: value});
      }
    } else if ($isElementNode(node)) {
      node.getChildren().forEach(visit);
    }
  };
  visit($getRoot());
  return mentions;
};

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const mentionPattern = (name: string) =>
  new RegExp(`@${escapeRegExp(name)}(?![\\p{L}\\p{N}])`, 'u');

/** The mentions whose `@Name` still appears in `text`, in order of appearance. */
export const mentionsInText = (text: string, mentions: ChatMention[]): ChatMention[] =>
  mentions
    .map((mention) => ({mention, index: text.search(mentionPattern(mention.name))}))
    .filter(({index}) => index >= 0)
    .sort((a, b) => a.index - b.index)
    .map(({mention}) => mention)
    .filter((mention, i, all) => all.findIndex((m) => m.employee_id === mention.employee_id) === i);

/** Splits `text` into plain strings and `@Name` mentions, for display. */
export const splitMentions = (
  text: string,
  mentions: ChatMention[]
): Array<string | ChatMention> => {
  const parts: Array<string | ChatMention> = [];
  let rest = text;
  for (;;) {
    const next = mentions
      .map((mention) => ({mention, match: mentionPattern(mention.name).exec(rest)}))
      .filter(({match}) => match !== null)
      // Earliest first; at the same spot "@Jane Doe" beats "@Jane".
      .sort(
        (a, b) => a.match!.index - b.match!.index || b.mention.name.length - a.mention.name.length
      )[0];
    if (!next?.match) {
      break;
    }
    if (next.match.index > 0) {
      parts.push(rest.slice(0, next.match.index));
    }
    parts.push(next.mention);
    rest = rest.slice(next.match.index + next.match[0].length);
  }
  if (rest) {
    parts.push(rest);
  }
  return parts;
};

/**
 * Remembers everyone picked in a composer so a message keeps its mentions after
 * the editor loses the mention nodes (a queued message edited back into the
 * composer, a canceled turn restored as text, a remount). At send time only the
 * people whose `@Name` is still in the text are used; a later pick of the same
 * name wins.
 */
export const useMentionMemory = () => {
  const byName = useRef(new Map<string, ChatMention>());
  return useMemo(
    () => ({
      remember: (mentions: ChatMention[]) => {
        mentions.forEach((mention) => byName.current.set(mention.name, mention));
      },
      resolve: (text: string) => mentionsInText(text, [...byName.current.values()]),
      forget: () => byName.current.clear(),
    }),
    []
  );
};
